/**
 * Paddle Billing checkout (overlay) via CDN paddle.js.
 * Requires PADDLE_CLIENT_TOKEN on the backend.
 *
 * Theming notes:
 * - `theme`: "light" | "dark" — matches your site's current theme.
 *   Set via Initialize settings. Dark theme gives a dark modal background.
 * - `frameStyle`: CSS string for the iframe container (border-radius, shadow).
 *   This wraps the outer frame; inner UI colors are Paddle-controlled.
 * - Brand color (button/link color) is set in Paddle Dashboard →
 *   Customisation → Checkout → Primary colour. Set it to match your site.
 */

import { getConverterApiBase } from "../api";

export type PaddleCheckoutSettings = {
  successUrl?: string;
};

declare global {
  interface Window {
    Paddle?: {
      Environment: { set: (env: string) => void };
      Initialize: (opts: {
        token: string;
        checkout?: {
          settings?: {
            displayMode?: string;
            theme?: "light" | "dark";
            locale?: string;
            frameTarget?: string;
            frameInitialHeight?: number;
            frameStyle?: string;
            successUrl?: string;
            allowedPaymentMethods?: string[];
          };
        };
        eventCallback?: (event: unknown) => void;
      }) => void;
      Checkout: {
        open: (opts: {
          transactionId: string;
          settings?: {
            successUrl?: string;
            theme?: "light" | "dark";
            frameStyle?: string;
          };
        }) => void;
      };
    };
  }
}

let paddleInitPromise: Promise<boolean> | null = null;
let _lastTheme: "light" | "dark" = "light";

/** Read the user's current theme from the DOM (set by ThemeProvider). */
function getCurrentTheme(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/**
 * The CSS applied to the outer Paddle iframe container.
 * This is the only visual we can tweak from code — Paddle renders the inner
 * content, but we can at least match the modal frame to your site's style.
 */
function getPaddleFrameStyle(theme: "light" | "dark"): string {
  return theme === "dark"
    ? "width: 100%; min-width: 312px; background-color: transparent; border: none; border-radius: 16px; overflow: hidden;"
    : "width: 100%; min-width: 312px; background-color: transparent; border: none; border-radius: 16px; overflow: hidden;";
}

export function extractTransactionId(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    return u.searchParams.get("_ptxn") || u.searchParams.get("paddle_txn");
  } catch {
    return null;
  }
}

/** True when Paddle hosts checkout (not our SPA URL). */
export function isHostedPaddleUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.includes("paddle.com") || host.startsWith("buy.");
  } catch {
    return false;
  }
}

function loadPaddleScript(): Promise<void> {
  if (window.Paddle) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-paddle-js="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Could not load Paddle checkout")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.async = true;
    script.dataset.paddleJs = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Paddle checkout"));
    document.head.appendChild(script);
  });
}

export async function ensurePaddleInitialized(): Promise<boolean> {
  const theme = getCurrentTheme();

  // If theme changed since last init, reset so we re-init with the new theme.
  // Paddle doesn't expose a method to change theme post-init, so we must re-init.
  if (window.Paddle && paddleInitPromise && theme !== _lastTheme) {
    paddleInitPromise = null;
  }

  // Skip re-init if Paddle is already live with the same theme
  if (window.Paddle && paddleInitPromise) return paddleInitPromise;

  // Don't re-use a cached failed promise — reset so the next call retries
  if (!window.Paddle) paddleInitPromise = null;

  if (!paddleInitPromise) {
    paddleInitPromise = (async () => {
      try {
        const res = await fetch(`${getConverterApiBase()}/api/subscription/paddle-token`);
        if (!res.ok) {
          paddleInitPromise = null;
          console.error(
            "[paddle] paddle-token failed:",
            res.status,
            await res.text().catch(() => ""),
          );
          return false;
        }
        const { token, sandbox } = (await res.json()) as { token?: string; sandbox?: boolean };
        if (!token?.trim()) { paddleInitPromise = null; return false; }

        await loadPaddleScript();
        if (!window.Paddle) { paddleInitPromise = null; return false; }

        if (sandbox) window.Paddle.Environment.set("sandbox");

        _lastTheme = theme;

        window.Paddle.Initialize({
          token: token.trim(),
          checkout: {
            settings: {
              displayMode: "overlay",
              theme,
              frameStyle: getPaddleFrameStyle(theme),
            },
          },
        });
        return true;
      } catch {
        // Reset so the next checkout attempt retries from scratch
        paddleInitPromise = null;
        return false;
      }
    })();
  }

  return paddleInitPromise;
}

/** Open Paddle overlay for a server-created transaction. */
export async function openPaddleTransactionCheckout(
  transactionId: string,
  settings?: PaddleCheckoutSettings,
): Promise<boolean> {
  const ready = await ensurePaddleInitialized();
  if (!ready || !window.Paddle) return false;

  const theme = getCurrentTheme();

  window.Paddle.Checkout.open({
    transactionId,
    settings: {
      ...(settings?.successUrl ? { successUrl: settings.successUrl } : {}),
      theme,
      frameStyle: getPaddleFrameStyle(theme),
    },
  });
  return true;
}
